/**
 * Act3StrategySelect — 策略选择面板
 */
import React from 'react';
import type { StrategyId, TrialCard } from '@shared/types/cards';
import { BASE_STRATEGIES, SPECIAL_STRATEGIES, type StrategyDefinition } from '@shared/constants/act3Strategies';

interface Act3StrategySelectProps {
  trial: TrialCard;
  energyA: number;
  energyB: number;
  usedSpecials: StrategyId[];
  entryType: string;
  onSelect: (strategy: StrategyId) => void;
}

export const Act3StrategySelect: React.FC<Act3StrategySelectProps> = ({
  trial,
  energyA,
  energyB,
  usedSpecials,
  entryType,
  onSelect,
}) => {
  const avgEnergy = (energyA + energyB) / 2;

  const isStrategyAvailable = (s: StrategyDefinition): boolean => {
    // Check energy
    if (s.energyCost > avgEnergy) return false;
    // Check if special already used
    if (s.isSpecial && usedSpecials.includes(s.id)) return false;
    // Check if trial supports this strategy
    if (!trial.strategyRewards[s.id] && s.isSpecial) return false;
    return true;
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:px-8">
      <div className="max-w-md mx-auto">
        <h3 className="text-sm font-bold text-[#2D2D2D] mb-1">选择应对策略</h3>
        <p className="text-[10px] text-[#9CA3AF] mb-4">
          面对「{trial.name}」，你打算怎么做？
        </p>

        {/* Base strategies */}
        <div className="space-y-2 mb-4">
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider font-medium">基础策略</span>
          {BASE_STRATEGIES.map(s => (
            <StrategyCard
              key={s.id}
              strategy={s}
              reward={trial.strategyRewards[s.id]}
              available={isStrategyAvailable(s)}
              onSelect={() => onSelect(s.id)}
            />
          ))}
        </div>

        {/* Special strategies */}
        <div className="space-y-2">
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider font-medium">特殊策略（限用1次）</span>
          {SPECIAL_STRATEGIES.map(s => {
            const available = isStrategyAvailable(s);
            const hasReward = !!trial.strategyRewards[s.id];
            return (
              <StrategyCard
                key={s.id}
                strategy={s}
                reward={trial.strategyRewards[s.id]}
                available={available && hasReward}
                locked={!hasReward}
                used={usedSpecials.includes(s.id)}
                onSelect={() => onSelect(s.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface StrategyCardProps {
  strategy: StrategyDefinition;
  reward?: { commitmentChange: number; energyCost: number; stabilityChange: number; risk?: string };
  available: boolean;
  locked?: boolean;
  used?: boolean;
  onSelect: () => void;
}

const StrategyCard: React.FC<StrategyCardProps> = ({ strategy, reward, available, locked, used, onSelect }) => {
  const disabled = !available || locked || used;

  return (
    <button
      disabled={disabled}
      onClick={onSelect}
      className={`
        w-full text-left p-3 rounded-xl border-2 transition-all
        ${disabled
          ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50'
          : 'border-[#E5E0D8] bg-white hover:border-coral-300 hover:shadow-md active:scale-[0.98] cursor-pointer'
        }
      `}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg">{strategy.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#2D2D2D]">{strategy.name}</span>
            {strategy.isSpecial && (
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">特殊</span>
            )}
            {used && (
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">已用</span>
            )}
            {locked && !used && (
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">不可用</span>
            )}
          </div>
          <p className="text-[10px] text-[#6B7280] mt-0.5">{strategy.description}</p>

          {/* Reward preview */}
          {reward && (
            <div className="flex gap-2 mt-1.5 text-[9px]">
              <span className={reward.commitmentChange >= 0 ? 'text-green-600' : 'text-red-500'}>
                承诺 {reward.commitmentChange > 0 ? '+' : ''}{reward.commitmentChange}
              </span>
              <span className="text-amber-600">
                精力 -{reward.energyCost}
              </span>
              <span className={reward.stabilityChange >= 0 ? 'text-blue-600' : 'text-red-500'}>
                稳定 {reward.stabilityChange > 0 ? '+' : ''}{reward.stabilityChange}
              </span>
            </div>
          )}

          {reward?.risk && (
            <p className="text-[9px] text-red-400 mt-1 italic">⚠ {reward.risk}</p>
          )}
        </div>
      </div>
    </button>
  );
};

export default Act3StrategySelect;
